import React from 'react';
import {useTheme} from '@mui/material';
import type {ShowThesaurusCode, ThesaurusId} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {IconButton, Layout, Spacer} from 'components';
import {FieldLabel} from 'components/fields/FieldLabel';
import {SearchCategoryButton} from 'feature/editThesaurus/components/SearchCategoryButton';
import {SearchResultRange} from 'feature/editThesaurus/components/SearchResultRange';
import {useSearchResultPaging} from 'feature/editThesaurus/hooks';
import {useThesaurusContext} from '../hooks';
import {SearchResultPage} from './SearchResultPage';

const pageSize = 10;

type Props = {
  thesaurusId: ThesaurusId;
  showCode?: ShowThesaurusCode;
};

export const SearchResultContainer: React.FC<Props> = ({thesaurusId}) => {
  const {t} = useLocalization();
  const {colors} = useTheme();
  const {thesaurus, searchResult} = useThesaurusContext(thesaurusId);
  const {
    resultByCategoriesPage,
    startItemIndex,
    resultCategories,
    canGoto,
    goto,
  } = useSearchResultPaging(thesaurus, searchResult, pageSize);

  const total = searchResult?.length ?? 0;
  const fromItem = startItemIndex + 1;
  const toItem = Math.min(fromItem + pageSize - 1, total);

  return (
    <Layout flex={1}>
      <FieldLabel
        label={`${t('extension.thesaurusEdit.search.label')} i ${t(`thesaurus.${thesaurus.id}.root.label`)}`}
        colorx={colors.burgundy}
      />
      <Layout pb={0.5} horizontal adjustCenter justifyContent={'space-between'}>
        <Layout horizontal adjustCenter adjustLeft wrap>
          {resultCategories
            .filter(c => c.count > 0)
            .map(category => (
              <Layout key={category.id} p={0.5}>
                <SearchCategoryButton
                  title={category.label}
                  count={category.count}
                  onClick={() => goto({categoryId: category.id})}
                />
              </Layout>
            ))}
        </Layout>
      </Layout>
      <Spacer size={0.5} />
      <SearchResultPage
        thesaurusId={thesaurusId}
        resultByCategories={resultByCategoriesPage}
        Pager={() =>
          total > pageSize ? (
            <Layout horizontal adjustCenter justifySelf={'flex-end'}>
              <IconButton
                icon="ChevronLeft"
                disabled={!canGoto('prevPage')}
                onClick={() => goto('prevPage')}
              />
              <SearchResultRange from={fromItem} to={toItem} count={total} />
              <IconButton
                icon="ChevronRight"
                disabled={!canGoto('nextPage')}
                onClick={() => goto('nextPage')}
              />
            </Layout>
          ) : (
            <Layout pr={5} pl={2}>
              <SearchResultRange from={fromItem} to={toItem} count={total} />
            </Layout>
          )
        }
      />
    </Layout>
  );
};
